import { $getMarkIDs, $isMarkNode, $wrapSelectionInMarkNode } from "@lexical/mark";
import { $dfs } from "@lexical/utils";
import { $createRangeSelection, $getRoot, $getSelection, $isRangeSelection, $setSelection } from "lexical";

import { makeUuid } from "~/utils/common";

const getMarkedTextNodes = (markId: string) =>
  $getRoot()
    .getAllTextNodes()
    .filter((textNode) => $getMarkIDs(textNode, 0)?.includes(markId));

export const markSelection = () => {
  const selection = $getSelection();
  if (!$isRangeSelection(selection) || selection.isCollapsed()) {
    return null;
  }
  const selectedText = selection.getTextContent();
  const markId = makeUuid();
  $wrapSelectionInMarkNode(selection, selection.isBackward(), markId);
  return { markId, selectedText };
};

export const removeMarkById = (markId: string) => {
  $dfs($getRoot()).forEach(({ node }) => {
    if (!$isMarkNode(node) || !node.getIDs().includes(markId)) {
      return;
    }
    if (node.getIDs().length === 1) {
      const children = node.getChildren();
      children.forEach((child) => {
        node.insertBefore(child);
      });
      node.remove();
    } else {
      node.deleteID(markId);
    }
  });
};

export const removeMarkAndSelectEnd = (markId: string): boolean => {
  const nodes = getMarkedTextNodes(markId);
  const lastTextNode = nodes.at(-1);
  if (!lastTextNode) {
    return false;
  }

  const selection = $createRangeSelection();
  selection.setTextNodeRange(nodes[0], 0, lastTextNode, lastTextNode.getTextContentSize());

  const shouldInsertBelow =
    selection.getTextContent().includes("\n") ||
    new Set(nodes.map((node) => node.getTopLevelElementOrThrow().getKey())).size > 1;

  removeMarkById(markId);
  if (lastTextNode.isAttached()) {
    lastTextNode.selectEnd();
  }

  return shouldInsertBelow;
};

export const replaceMarkedSelection = (markId: string) => {
  const nodes = getMarkedTextNodes(markId);
  if (!nodes.length) {
    return;
  }
  const firstTextNode = nodes[0];
  const lastTextNode = nodes[nodes.length - 1];
  const selection = $createRangeSelection();
  selection.setTextNodeRange(firstTextNode, 0, lastTextNode, lastTextNode.getTextContentSize());
  $setSelection(selection);
  selection.removeText();
  removeMarkById(markId);
};
